import React, { useEffect, useState } from "react";
import { Form, Input, Button, Select, DatePicker, TimePicker, message, Row, Col, Card, Typography, Space, Table, Checkbox } from "antd";
import { PlusOutlined, SaveOutlined, EditOutlined, DeleteOutlined, ClockCircleOutlined, TeamOutlined } from "@ant-design/icons";
import API from "../../api";
import moment from "moment";

const { Title, Text } = Typography;
const { Option } = Select;

const Attendance = () => {
    const [form] = Form.useForm();
    const [isLoading, setLoading] = useState(false);
    const [stores, setStores] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [employeeAttendance, setEmployeeAttendance] = useState([]);

    // Fetch stores
    const getStores = async () => {
        try {
            const response = await API.get("/stores");
            setStores(response?.data || []);
        } catch (err) {
            console.error("Error fetching stores:", err);
            message.error("Failed to load stores.");
        }
    };

    // Fetch employees by store
    const getEmployeesByStore = async (storeId) => {
        if (!storeId) return;
        try {
            const response = await API.get(`/employee/by-store`, {
                params: {
                    storeId: storeId
                }
            });
            const employeesData = response?.data || [];
            setEmployees(employeesData);
            
            // Initialize attendance for all employees as present
            const initialAttendance = employeesData.map(emp => ({
                employeeId: emp._id,
                employeeName: emp.name,
                designation: emp.designation,
                isPresent: true,
                individualInTime: null,
                individualOutTime: null,
                workingHours: 0,
                status: 'not_done',
                remarks: "",
                intimeSaved: false,
                outtimeSaved: false,
                attendanceId: null
            }));
            setEmployeeAttendance(initialAttendance);
        } catch (err) {
            console.error("Error fetching employees:", err);
            message.error("Failed to load employees.");
        }
    };

    // Clear all attendance data to initial state
    const clearAttendanceData = () => {
        console.log('🧹 Clearing all attendance data to initial state');
        setEmployeeAttendance(prev => 
            prev.map(emp => ({
                ...emp,
                individualInTime: null,
                individualOutTime: null,
                workingHours: 0,
                status: 'not_done',
                intimeSaved: false,
                outtimeSaved: false,
                attendanceId: null
            }))
        );
    };

    // Load existing attendance data for selected store and date
    const loadExistingAttendance = async (storeId, date) => {
        if (!storeId || !date) return;
        
        try {
            const formattedDate = date.format('YYYY-MM-DD');
            console.log('🔍 Loading existing attendance for Store ID:', storeId, 'Date:', formattedDate);
            
            const response = await API.get(`/attendance/store/${storeId}/date/${formattedDate}`);
            console.log('📡 API Response:', response);
            
            if (response.data.success && response.data.data.length > 0) {
                const existingAttendance = response.data.data;
                console.log('✅ Found existing attendance records:', existingAttendance.length);
                
                setEmployeeAttendance(prev => 
                    prev.map(emp => {
                        const existing = existingAttendance.find(att => att.employeeId._id === emp.employeeId);
                        if (existing) {
                            console.log('👤 Employee:', emp.employeeName, 'Existing data:', existing);
                            return {
                                ...emp,
                                individualInTime: moment(existing.inTime, 'HH:mm'),
                                individualOutTime: existing.outTime ? moment(existing.outTime, 'HH:mm') : null,
                                workingHours: existing.workingHours || 0,
                                isPresent: existing.isPresent,
                                remarks: existing.remarks || "",
                                status: existing.status || 'intime_saved',
                                intimeSaved: true,
                                outtimeSaved: existing.outTime ? true : false,
                                attendanceId: existing._id
                            };
                        }
                        // No existing data for this employee, keep initial state
                        console.log('👤 Employee:', emp.employeeName, 'No existing data - keeping initial state');
                        return emp;
                    })
                );
            } else {
                console.log('ℹ️ No existing attendance found for this store and date - all employees start fresh');
                // Ensure all employees are in initial state when no data exists
                clearAttendanceData();
            }
        } catch (err) {
            console.error("❌ Error loading existing attendance:", err);
            // On error, ensure all employees are in initial state
            clearAttendanceData();
        }
    };

    // Handle store change
    const handleStoreChange = (storeId) => {
        console.log('🏪 Store changed to:', storeId);
        setSelectedStoreId(storeId);
        setEmployees([]);
        setEmployeeAttendance([]);
        if (storeId) {
            getEmployeesByStore(storeId);
            // Load existing attendance if date is already selected
            if (selectedDate) {
                console.log('📅 Date already selected, loading existing attendance for new store...');
                // Wait for employees to load, then load attendance
                setTimeout(() => {
                    loadExistingAttendance(storeId, selectedDate);
                }, 100);
            }
        }
    };

    // Handle date change
    const handleDateChange = (date) => {
        console.log('📅 Date changed to:', date ? date.format('YYYY-MM-DD') : 'null');
        
        // Additional validation: prevent future dates
        if (date && date.isAfter(moment().endOf('day'))) {
            message.error("Future dates are not allowed for attendance!");
            return;
        }
        
        setSelectedDate(date);
        
        // Clear existing attendance data when date changes
        if (selectedStoreId && date) {
            console.log('🏪 Store already selected, loading existing attendance for new date...');
            // Clear all data first, then load for new date
            clearAttendanceData();
            // Then load existing data for the new date
            loadExistingAttendance(selectedStoreId, date);
        }
    };



    // Handle individual employee save
    const handleIndividualSave = async (employeeId) => {
        if (!selectedDate) {
            message.error("Please select a date first.");
            return;
        }

        // Additional validation: prevent saving for future dates
        if (selectedDate.isAfter(moment().endOf('day'))) {
            message.error("Cannot save attendance for future dates!");
            return;
        }

        const employee = employeeAttendance.find(emp => emp.employeeId === employeeId);
        if (!employee) return;

        // Check for abnormal working hours and show warning
        if (employee.individualInTime && employee.individualOutTime) {
            const workingHours = calculateWorkingHours(
                employee.individualInTime.format('HH:mm'), 
                employee.individualOutTime.format('HH:mm')
            );
            
            if (workingHours > 0 && (workingHours > 10 || workingHours < 8)) {
                const warningMessage = workingHours > 10 
                    ? `Warning: Working hours (${workingHours.toFixed(2)} hrs) exceed 10 hours. This may be incorrect.`
                    : `Warning: Working hours (${workingHours.toFixed(2)} hrs) are less than 8 hours. This may be incorrect.`;
                
                message.warning(warningMessage, 5); // Show warning for 5 seconds
            }
        }

        // If employee is marked as absent, allow saving without time entries
        if (!employee.isPresent) {
            // Save absent record
            const attendanceData = {
                storeId: selectedStoreId,
                employeeId: employee.employeeId,
                date: selectedDate.format('YYYY-MM-DD'),
                inTime: null, // No in time for absent
                outTime: null, // No out time for absent
                workingHours: 0,
                isPresent: false,
                remarks: employee.remarks || "Absent"
            };

            console.log('Sending absent data to API:', attendanceData);
            
            try {
                const response = await API.post('/attendance', attendanceData);
                console.log('API response:', response);
                
                if (response.data.success) {
                    // Update local state to show absent is saved
                    setEmployeeAttendance(prev => 
                        prev.map(emp => 
                            emp.employeeId === employeeId 
                                ? { 
                                    ...emp, 
                                    status: 'completed',
                                    intimeSaved: true,
                                    outtimeSaved: true,
                                    attendanceId: response.data.data._id
                                  }
                                : emp
                        )
                    );

                    message.success(`Absent marked for ${employee.employeeName} successfully!`);
                }
            } catch (err) {
                console.error('Error saving absent record:', err);
                message.error(err.response?.data?.message || err.message || "Failed to save absent record.");
            }
            return;
        }

        // For present employees, require in time
        if (!employee.individualInTime) {
            message.error("Please select in time first.");
            return;
        }

        setLoading(true);
        try {
                        // Check if this is first entry (only in time) or second entry (both times)
            if (employee.individualInTime && !employee.individualOutTime) {
                // First entry: Save only in time
                const attendanceData = {
                    storeId: selectedStoreId,
                    employeeId: employee.employeeId,
                    date: selectedDate.format('YYYY-MM-DD'),
                    inTime: employee.individualInTime.format('HH:mm'),
                    outTime: null, // No out time yet
                    workingHours: 0,
                    isPresent: employee.isPresent,
                    remarks: employee.remarks || ""
                };

                console.log('Sending in time data to API:', attendanceData);
                
                // Save in time to database via API
                const response = await API.post('/attendance', attendanceData);
                console.log('API response:', response);
                
                if (response.data.success) {
                    // Update local state to show in time is saved
                    setEmployeeAttendance(prev => 
                        prev.map(emp => 
                            emp.employeeId === employeeId 
                                ? { 
                                    ...emp, 
                                    status: 'intime_saved',
                                    intimeSaved: true,
                                    attendanceId: response.data.data._id
                                  }
                                : emp
                        )
                    );

                    message.success(`In time recorded for ${employee.employeeName} successfully!`);
                }
            } else if (employee.individualInTime && employee.individualOutTime) {
                // Second entry: Update existing attendance with out time
                const workingHours = calculateWorkingHours(
                    employee.individualInTime.format('HH:mm'), 
                    employee.individualOutTime.format('HH:mm')
                );

                // Prepare data for API (this will update existing record)
                const attendanceData = {
                    storeId: selectedStoreId,
                    employeeId: employee.employeeId,
                    date: selectedDate.format('YYYY-MM-DD'),
                    inTime: employee.individualInTime.format('HH:mm'),
                    outTime: employee.individualOutTime.format('HH:mm'),
                    workingHours: workingHours,
                    isPresent: employee.isPresent,
                    remarks: employee.remarks || ""
                };

                console.log('Sending out time data to API:', attendanceData);
                
                // Update existing attendance record via API
                const response = await API.post('/attendance', attendanceData);
                console.log('API response:', response);
                
                if (response.data.success) {
                    // Update the employee attendance status to completed
                    setEmployeeAttendance(prev => 
                        prev.map(emp => 
                            emp.employeeId === employeeId 
                                ? { 
                                    ...emp, 
                                    status: 'completed', 
                                    lastSaved: new Date(),
                                    workingHours: workingHours,
                                    outtimeSaved: true
                                  }
                                : emp
                        )
                    );

                    message.success(`Attendance completed for ${employee.employeeName}! Working hours: ${workingHours.toFixed(2)} hours`);
                }
            } else {
                message.error("Please select in time first.");
                return;
            }
        } catch (err) {
            console.error('Error saving attendance:', err);
            console.error('Error response:', err.response);
            console.error('Error message:', err.message);
            message.error(err.response?.data?.message || err.message || "Failed to save attendance record.");
        } finally {
            setLoading(false);
        }
    };

    // Handle individual time updates
    const handleIndividualTimeUpdate = (employeeId, field, value) => {
        setEmployeeAttendance(prev => 
            prev.map(emp => 
                emp.employeeId === employeeId 
                    ? { ...emp, [field]: value, status: 'modified' }
                    : emp
            )
        );
    };

    // Handle employee attendance change
    const handleEmployeeAttendanceChange = (employeeId, field, value) => {
        setEmployeeAttendance(prev => 
            prev.map(emp => 
                emp.employeeId === employeeId 
                    ? { 
                        ...emp, 
                        [field]: value, 
                        status: 'modified',
                        // Clear time fields if marked as absent
                        ...(field === 'isPresent' && value === false && {
                            individualInTime: null,
                            individualOutTime: null,
                            workingHours: 0
                        })
                      }
                    : emp
            )
        );
    };

    // Get status text and color
    const getStatusInfo = (status, individualInTime, individualOutTime, workingHours, isPresent) => {
        if (!status || status === 'not_done') return { text: 'Not Done', color: '#ff4d4f' };
        
        // Handle absent employees
        if (isPresent === false) {
            return { text: 'Absent', color: '#ff4d4f' };
        }
        
        // Calculate working hours if both times are present but workingHours is 0
        let calculatedHours = workingHours;
        if (individualInTime && individualOutTime && workingHours === 0) {
            calculatedHours = calculateWorkingHours(
                individualInTime.format('HH:mm'), 
                individualOutTime.format('HH:mm')
            );
        }
        
        // Determine color for working hours
        const getWorkingHoursColor = (hours) => {
            if (hours > 0 && (hours > 10 || hours < 8)) {
                return '#ff4d4f'; // Red for >10hrs or <8hrs
            }
            return '#52c41a'; // Green for normal hours (8-10)
        };
        
        switch (status) {
            case 'intime_saved':
                if (individualInTime) {
                    return { text: `In: ${individualInTime.format('hh:mm A')}`, color: '#faad14' };
                }
                return { text: 'In Time Saved', color: '#faad14' };
            case 'saved':
                if (calculatedHours > 0) {
                    return { text: `${calculatedHours.toFixed(2)} hrs`, color: getWorkingHoursColor(calculatedHours) };
                }
                if (individualInTime && individualOutTime) {
                    return { text: `${individualInTime.format('hh:mm A')} - ${individualOutTime.format('hh:mm A')}`, color: '#faad14' };
                }
                return { text: 'Saved', color: '#52c41a' };
            case 'completed':
                if (calculatedHours > 0) {
                    return { text: `${calculatedHours.toFixed(2)} hrs`, color: getWorkingHoursColor(calculatedHours) };
                }
                if (individualInTime && individualOutTime) {
                    return { text: `${individualInTime.format('hh:mm A')} - ${individualOutTime.format('hh:mm A')}`, color: '#52c41a' };
                }
                return { text: 'Completed', color: '#52c41a' };
            case 'modified':
                if (calculatedHours > 0) {
                    return { text: `${calculatedHours.toFixed(2)} hrs (Modified)`, color: getWorkingHoursColor(calculatedHours) };
                }
                if (individualInTime && individualOutTime) {
                    return { text: `${individualInTime.format('hh:mm A')} - ${individualOutTime.format('hh:mm A')}`, color: '#faad14' };
                }
                return { text: 'Modified', color: '#faad14' };
            default:
                return { text: 'Not Done', color: '#ff4d4f' };
        }
    };

    // Calculate working hours
    const calculateWorkingHours = (inTime, outTime) => {
        if (!inTime || !outTime) return 0;
        
        const inMoment = moment(inTime, 'HH:mm');
        const outMoment = moment(outTime, 'HH:mm');
        
        if (outMoment.isBefore(inMoment)) {
            // If out time is before in time, assume it's the next day
            outMoment.add(1, 'day');
        }
        
        const duration = moment.duration(outMoment.diff(inMoment));
        return duration.asHours();
    };

    // Table columns for employee attendance
    const employeeAttendanceColumns = [
        {
            title: "Emp Name",
            dataIndex: "employeeName",
            key: "employeeName",
            render: (name, record) => (
                <div>
                    <Text strong>{name}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        {record.designation}
                    </Text>
                </div>
            ),
        },
        {
            title: "Intime",
            dataIndex: "individualInTime",
            key: "individualInTime",
            render: (time, record) => (
                <TimePicker 
                    style={{ width: "100%" }} 
                    format="hh:mm A"
                    use12Hours
                    placeholder="Select In Time"
                    value={time}
                    onChange={(value) => handleIndividualTimeUpdate(record.employeeId, 'individualInTime', value)}
                    size="small"
                    disabled={!record.isPresent}
                />
            ),
        },
        {
            title: "Outtime",
            dataIndex: "individualOutTime",
            key: "individualOutTime",
            render: (time, record) => (
                <TimePicker 
                    style={{ width: "100%" }} 
                    format="hh:mm A"
                    use12Hours
                    placeholder="Select Out Time"
                    value={time}
                    onChange={(value) => handleIndividualTimeUpdate(record.employeeId, 'individualOutTime', value)}
                    size="small"
                    disabled={!record.isPresent}
                />
            ),
        },
        {
            title: "Working Hours",
            dataIndex: "workingHours",
            key: "workingHours",
            width: 120,
            render: (hours, record) => {
                // Calculate working hours if both in and out times are present
                let workingHours = record.workingHours;
                if (record.individualInTime && record.individualOutTime && workingHours === 0) {
                    workingHours = calculateWorkingHours(
                        record.individualInTime.format('HH:mm'), 
                        record.individualOutTime.format('HH:mm')
                    );
                }

                // Determine color based on working hours
                let textColor = '#52c41a'; // Default green
                if (workingHours > 0 && (workingHours > 10 || workingHours < 8)) {
                    textColor = '#ff4d4f'; // Red for >10hrs or <8hrs
                }

                return (
                    <div>
                        {!record.isPresent ? (
                            <Text type="danger" style={{ fontSize: "12px" }}>
                                Absent
                            </Text>
                        ) : workingHours > 0 ? (
                            <Text strong style={{ color: textColor }}>
                                {workingHours.toFixed(2)} hrs
                            </Text>
                        ) : (
                            <Text type="secondary">-</Text>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Present/Absent",
            dataIndex: "isPresent",
            key: "isPresent",
            width: 120,
            render: (isPresent, record) => (
                <Checkbox
                    checked={isPresent}
                    onChange={(e) => handleEmployeeAttendanceChange(record.employeeId, 'isPresent', e.target.checked)}
                />
            ),
        },
        {
            title: "Remarks",
            dataIndex: "remarks",
            key: "remarks",
            render: (remarks, record) => (
                <Input
                    placeholder={record.isPresent ? "Optional remarks..." : "Reason for absence..."}
                    value={remarks}
                    onChange={(e) => handleEmployeeAttendanceChange(record.employeeId, 'remarks', e.target.value)}
                    size="small"
                />
            ),
        },
        {
            title: "Action",
            key: "action",
            width: 150,
            render: (_, record) => {
                const statusInfo = getStatusInfo(record.status, record.individualInTime, record.individualOutTime, record.workingHours, record.isPresent);
                
                return (
                    <div>
                        <Button
                            type="primary"
                            size="small"
                            onClick={() => handleIndividualSave(record.employeeId)}
                            loading={isLoading}
                            disabled={!selectedDate || (record.isPresent && record.intimeSaved && !record.individualOutTime)}
                        >
                            {!record.isPresent ? 'Mark Absent' : (!record.intimeSaved ? 'Save In Time' : 'Save Out Time')}
                        </Button>
                        {record.intimeSaved && !record.outtimeSaved && (
                            <div style={{ marginTop: "4px" }}>
                                <Text type="secondary" style={{ fontSize: "10px" }}>
                                    In time already saved
                                </Text>
                            </div>
                        )}
                        <div style={{ marginTop: "4px" }}>
                            <Text style={{ 
                                color: statusInfo.color, 
                                fontSize: "11px",
                                fontWeight: "500"
                            }}>
                                {statusInfo.text}
                            </Text>
                        </div>
                    </div>
                );
            },
        },
    ];

    useEffect(() => {
        getStores();
    }, []);

    return (
        <div style={{
            padding: "20px",
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}>
            {/* <Title level={3} style={{ marginBottom: "24px" }}>
                <ClockCircleOutlined style={{ marginRight: "8px" }} />
                Employee Attendance Management
            </Title> */}
            
            <Card title="Mark Attendance" style={{ marginBottom: "24px" }}>
                <Row gutter={16} style={{ marginBottom: "24px" }}>
                    <Col span={12}>
                        <Form.Item
                            label="Select Store/Branch"
                            name="storeId"
                            rules={[{ required: true, message: "Please select a store." }]}
                        >
                            <Select
                                placeholder="Select Store"
                                allowClear
                                onChange={handleStoreChange}
                            >
                                {stores.map((store) => (
                                    <Select.Option key={store._id} value={store._id}>
                                        {store.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Date"
                            name="date"
                            rules={[{ required: true, message: "Please select date." }]}
                        >
                            <DatePicker 
                                style={{ width: "100%" }} 
                                onChange={handleDateChange}
                                placeholder="Select Date"
                                disabledDate={(current) => {
                                    // Disable future dates
                                    return current && current > moment().endOf('day');
                                }}
                            />
                            <div style={{ marginTop: "4px" }}>
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    ⚠️ Future dates are not allowed for attendance
                                </Text>
                            </div>
                        </Form.Item>
                    </Col>
                </Row>

                {employeeAttendance.length > 0 && (
                    <Card 
                        title={
                            <Space>
                                <TeamOutlined />
                                <Text>Employee Attendance ({employeeAttendance.length} employees)</Text>
                            </Space>
                        } 
                        size="small" 
                        style={{ marginBottom: "24px" }}
                    >
                        <Table
                            columns={employeeAttendanceColumns}
                            dataSource={employeeAttendance}
                            rowKey="employeeId"
                            pagination={false}
                            size="small"
                            scroll={{ x: 900 }}
                        />
                    </Card>
                )}
            </Card>
        </div>
    );
};

export default Attendance;
